import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { View, StyleSheet, Pressable, TextInput } from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { Toggle } from "@/components/ui/Toggle";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import { getMonthName, sameDay } from "@/lib/helpers";

type CompactDateTimePickerProps = {
  date: Date;
  onPick: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
};

// Resolve hours/minutes text + ampm into 24h values
function resolveTime(hoursText: string, minutesText: string, ampm: string, use24h: boolean) {
  let h = parseInt(hoursText, 10) || 0;
  let m = parseInt(minutesText, 10) || 0;
  if (use24h) {
    h = Math.max(0, Math.min(23, h));
  } else {
    h = Math.max(1, Math.min(12, h));
    if (ampm === "AM") {
      h = h === 12 ? 0 : h;
    } else {
      h = h === 12 ? 12 : h + 12;
    }
  }
  m = Math.max(0, Math.min(59, m));
  return { h, m };
}

export function CompactDateTimePicker({ date, onPick, minDate, maxDate }: CompactDateTimePickerProps) {
  const { colors } = useTheme();
  const { settings } = useDateTime();
  const use24h = settings.timeFormat === "24";
  const now = useMemo(() => new Date(), []);

  // Calendar nav
  const [navYear, setNavYear] = useState(date.getFullYear());
  const [navMonth, setNavMonth] = useState(date.getMonth() + 1);

  // Selection (date)
  const [selYear, setSelYear] = useState(date.getFullYear());
  const [selMonth, setSelMonth] = useState(date.getMonth() + 1);
  const [selDay, setSelDay] = useState(date.getDate());

  // Time
  const initH24 = date.getHours();
  const initM = date.getMinutes();
  const [ampm, setAmPm] = useState(initH24 >= 12 ? "PM" : "AM");
  const [hoursText, setHoursText] = useState(String(use24h ? initH24 : initH24 % 12 || 12).padStart(2, "0"));
  const [minutesText, setMinutesText] = useState(String(initM).padStart(2, "0"));

  // Mirror all mutable state into a ref so the unmount cleanup can read latest values
  const latestRef = useRef({ selYear, selMonth, selDay, hoursText, minutesText, ampm });
  useEffect(() => {
    latestRef.current = { selYear, selMonth, selDay, hoursText, minutesText, ampm };
  }, [selYear, selMonth, selDay, hoursText, minutesText, ampm]);

  // Track the last emitted h/m so we only flush genuinely dirty values
  const lastEmittedRef = useRef({ h: initH24, m: initM });

  // Flush any dirty time input on unmount
  useEffect(() => {
    return () => {
      const { selYear: y, selMonth: mo, selDay: d, hoursText: hT, minutesText: mT, ampm: ap } = latestRef.current;
      const { h, m } = resolveTime(hT, mT, ap, use24h);
      if (h !== lastEmittedRef.current.h || m !== lastEmittedRef.current.m) {
        onPick(new Date(y, mo - 1, d, h, m, 0, 0));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build a Date from given date parts + current time state and fire onPick
  const emitWithDate = useCallback(
    (y: number, mo: number, d: number) => {
      const { h, m } = resolveTime(hoursText, minutesText, ampm, use24h);
      lastEmittedRef.current = { h, m };
      onPick(new Date(y, mo - 1, d, h, m, 0, 0));
    },
    [hoursText, minutesText, ampm, use24h, onPick],
  );

  // Build a Date from current date state + given time parts and fire onPick
  const emitWithTime = useCallback(
    (hText: string, mText: string, ap: string) => {
      const { h, m } = resolveTime(hText, mText, ap, use24h);
      lastEmittedRef.current = { h, m };
      onPick(new Date(selYear, selMonth - 1, selDay, h, m, 0, 0));
    },
    [selYear, selMonth, selDay, use24h, onPick],
  );

  // Check if a date is disabled
  const isDateDisabled = (d: Date): boolean => {
    if (minDate) {
      const minOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (dOnly < minOnly) return true;
    }
    if (maxDate) {
      const maxOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (dOnly > maxOnly) return true;
    }
    return false;
  };

  const setMonthSafe = (next: number) => {
    if (next < 1) {
      setNavMonth(12);
      setNavYear((y) => y - 1);
    } else if (next > 12) {
      setNavMonth(1);
      setNavYear((y) => y + 1);
    } else {
      setNavMonth(next);
    }
  };

  const daysInMonth = new Date(navYear, navMonth, 0).getDate();
  const firstDow = new Date(navYear, navMonth - 1, 1).getDay();

  const days = Array.from({ length: daysInMonth }, (_, i) => ({
    date: new Date(navYear, navMonth - 1, i + 1),
  }));

  const leadingDays = Array.from({ length: firstDow }, (_, i) => {
    const prevMonth = new Date(navYear, navMonth - 1, 0);
    return { date: new Date(navYear, navMonth - 2, prevMonth.getDate() - firstDow + i + 1), muted: true };
  });

  const selectDate = (d: Date) => {
    const y = d.getFullYear();
    const mo = d.getMonth() + 1;
    const day = d.getDate();
    setSelYear(y);
    setSelMonth(mo);
    setSelDay(day);
    emitWithDate(y, mo, day);
  };

  const handleHoursBlur = () => {
    // Clamp and pad on blur, then emit
    let h = parseInt(hoursText, 10) || 0;
    const max = use24h ? 23 : 12;
    const min = use24h ? 0 : 1;
    h = Math.max(min, Math.min(max, h));
    const clamped = String(h).padStart(2, "0");
    setHoursText(clamped);
    emitWithTime(clamped, minutesText, ampm);
  };

  const handleMinutesBlur = () => {
    let m = parseInt(minutesText, 10) || 0;
    m = Math.max(0, Math.min(59, m));
    const clamped = String(m).padStart(2, "0");
    setMinutesText(clamped);
    emitWithTime(hoursText, clamped, ampm);
  };

  const handleAmPmChange = (v: string) => {
    setAmPm(v as "AM" | "PM");
    emitWithTime(hoursText, minutesText, v);
  };

  return (
    <View style={styles.container}>
      {/* Compact calendar header */}
      <View style={styles.calHeader}>
        <Pressable
          style={({ hovered }: any) => [
            styles.stepBtn,
            { borderColor: colors.border },
            hovered && { backgroundColor: colors.border },
          ]}
          onPress={() => setMonthSafe(navMonth - 1)}
        >
          <MaterialIcons name="chevron-left" size={20} color={colors.text} />
        </Pressable>
        <View style={styles.calHeaderCenter}>
          <ThemedText style={styles.headerText}>
            {getMonthName(navMonth, true)} {navYear}
          </ThemedText>
        </View>
        <Pressable
          style={({ hovered }: any) => [
            styles.stepBtn,
            { borderColor: colors.border },
            hovered && { backgroundColor: colors.border },
          ]}
          onPress={() => setMonthSafe(navMonth + 1)}
        >
          <MaterialIcons name="chevron-right" size={20} color={colors.text} />
        </Pressable>
      </View>

      {/* Days of week */}
      <View style={styles.dowRow}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <ThemedText key={i} style={[styles.dow, { color: colors.placeholder }]}>
            {d}
          </ThemedText>
        ))}
      </View>

      {/* Calendar grid - compact */}
      <View style={styles.grid}>
        {leadingDays.map(({ date: d }, i) => (
          <Button
            key={`l${i}`}
            text={`${d.getDate()}`}
            style={[styles.dayCell, styles.mutedCell]}
            textStyle={[styles.dayText, { color: colors.placeholder }]}
            onPress={() => {
              setNavMonth(d.getMonth() + 1);
              setNavYear(d.getFullYear());
              selectDate(d);
            }}
          />
        ))}
        {days.map(({ date: d }, i) => {
          const isToday = sameDay(d, now);
          const isSelected = d.getFullYear() === selYear && d.getMonth() + 1 === selMonth && d.getDate() === selDay;
          const disabled = isDateDisabled(d);

          return (
            <Button
              key={`d${i}`}
              text={`${d.getDate()}`}
              enabled={!disabled}
              style={[
                styles.dayCell,
                isSelected && { backgroundColor: colors.primary },
                isToday && !isSelected && { backgroundColor: colors.secondary },
                disabled && { opacity: 0.3 },
              ]}
              textStyle={[
                styles.dayText,
                (isSelected || isToday) && { color: "white", fontWeight: "600" },
                disabled && { textDecorationLine: "line-through" },
              ]}
              onPress={() => !disabled && selectDate(d)}
            />
          );
        })}
      </View>

      {/* Time input row */}
      <View style={[styles.timeRow, { borderColor: colors.border }]}>
        <TextInput
          style={[styles.timeInput, { color: colors.text, borderColor: colors.border }]}
          value={hoursText}
          onChangeText={(t) => setHoursText(t.replace(/[^\d]/g, "").slice(0, 2))}
          onBlur={handleHoursBlur}
          keyboardType="numeric"
          maxLength={2}
          textAlign="center"
        />
        <ThemedText style={styles.colon}>:</ThemedText>
        <TextInput
          style={[styles.timeInput, { color: colors.text, borderColor: colors.border }]}
          value={minutesText}
          onChangeText={(t) => setMinutesText(t.replace(/[^\d]/g, "").slice(0, 2))}
          onBlur={handleMinutesBlur}
          keyboardType="numeric"
          maxLength={2}
          textAlign="center"
        />
        {!use24h && <Toggle style={styles.ampmToggle} choices={["AM", "PM"]} value={ampm} onPick={handleAmPmChange} />}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 220,
    gap: 6,
  },
  calHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  stepBtn: {
    height: 24,
    width: 24,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
  },
  calHeaderCenter: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    fontSize: 12,
    fontWeight: "600",
  },
  dowRow: {
    flexDirection: "row",
    gap: 2,
  },
  dow: {
    flex: 1,
    textAlign: "center",
    fontSize: 10,
    fontWeight: "600",
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2,
  },
  dayCell: {
    width: 28,
    height: 28,
    minWidth: 28,
    minHeight: 28,
    borderRadius: 6,
    padding: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  dayText: {
    fontSize: 11,
  },
  mutedCell: {
    opacity: 0.4,
  },
  timeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingTop: 6,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  timeInput: {
    width: 36,
    height: 28,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
    fontSize: 13,
    fontWeight: "500",
    textAlign: "center",
    paddingHorizontal: 4,
  },
  colon: {
    fontSize: 14,
    fontWeight: "600",
  },
  ampmToggle: {
    marginLeft: 4,
  },
});
